import classNames from 'clsx';
import React, { type ReactElement, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DOM_ID_PHONE_HEADER_ERRORS_PAGE } from 'reactApp/appHelpers/advertisingConfigHelpers';
import { IS_BLOCKED } from 'reactApp/appHelpers/configHelpers';
import { enabledPublicDownloadLimits } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadLimitsInformer from 'reactApp/components/DownloadLimitsInformer/DownloadLimitsInformer';
import { usePromoBlock } from 'reactApp/hooks/mobile/usePromoBlock';
import { isDefaultRootContent } from 'reactApp/modules/home/home.selectors';
import { checkUpdateLicenseRequest } from 'reactApp/modules/profile/profile.module';
import { getFirstPublicWithDownloadLeftZero } from 'reactApp/modules/public/public.selectors';
import { selectStatusPage } from 'reactApp/modules/router/router.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isUploaderVisible } from 'reactApp/modules/uploadList/uploadList.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ErrorPage } from 'reactApp/sections/ErrorPage/ErrorPage';
import { useRouteChangeProcessing } from 'reactApp/sections/MainPage/hooks/useRouteChangeProcessing';
import type { RootState } from 'reactApp/store';
import { ActionsBar, EActionsMode } from 'reactApp/ui/Mobile/ActionsBar/ActionsBar';
import { ActionsCarousel } from 'reactApp/ui/Mobile/ActionsCarousel/ActionsCarousel';
import { BreadcrumbsContainer as Breadcrumbs } from 'reactApp/ui/Mobile/Breadcrumbs/BreadcrumbsContainer';
import { Datalist } from 'reactApp/ui/Mobile/Datalist/Datalist';
import { Toolbar } from 'reactApp/ui/Mobile/Toolbar/Toolbar';
import { UploadDropArea } from 'reactApp/ui/UploadDropArea/UploadDropArea';
import { UploadListMobileContainer } from 'reactApp/ui/UploadList/UploadListMobile';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './MobileHomePage.css';

export const MobileHomePage = memo(function MobileHomePage(): ReactElement | null {
    const dispatch = useDispatch();
    const location = useLocation();
    const isSelecting = useSelector(SelectionsSelectors.isSelecting);
    const selectedIds = useSelector(SelectionsSelectors.getSelectedIdxs);
    const isDefaultContent = useSelector(isDefaultRootContent);
    const statusPage = useSelector(selectStatusPage);
    const showUploader = useSelector(isUploaderVisible);
    const showActionsBar = selectedIds.length > 0;
    const showUploadArea = !showUploader && !showActionsBar;
    const isFrozen = useSelector(UserSelectors.isFrozen);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);

    // tempexp_17847-start
    const firstPublicWithDownloadLeftZero = useSelector((state: RootState) => getFirstPublicWithDownloadLeftZero(state));
    const valuePublicLimits = firstPublicWithDownloadLeftZero && Object.values(firstPublicWithDownloadLeftZero)[0];
    const leftDownloads = valuePublicLimits && valuePublicLimits.left;
    const totalDownloads = valuePublicLimits && valuePublicLimits.total;
    const showDownloadLimitsInformer = enabledPublicDownloadLimits && !isPaidUser && leftDownloads === 0;
    // tempexp_17847-end

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'touch-home' });
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: 'home' });
    }, []);

    useEffect(() => {
        if (isDefaultContent) {
            dispatch(checkUpdateLicenseRequest());
        }
    }, [isDefaultContent]);

    useRouteChangeProcessing({ storage: EStorageType.home, path: location.pathname, search: location.search });

    const promoBlock = usePromoBlock();

    if (IS_BLOCKED || isFrozen) {
        return null;
    }

    if (statusPage) {
        return (
            <div className={classNames(styles.root, styles.root_error)}>
                <div id={DOM_ID_PHONE_HEADER_ERRORS_PAGE} />
                <Toolbar disableActions />
                <ErrorPage status={statusPage} />
            </div>
        );
    }

    return (
        <>
            <div className={styles.root}>
                <Toolbar showMenu />
                {promoBlock}
                <div className={styles.content}>
                    <Breadcrumbs />
                    <ActionsCarousel disabled={isSelecting} />
                    {showDownloadLimitsInformer && <DownloadLimitsInformer isMobile totalDownloads={totalDownloads} />}
                    <Datalist />
                </div>
            </div>
            {showActionsBar && <ActionsBar mode={EActionsMode.PRIMARY} />}
            {showUploader && <UploadListMobileContainer />}
            {showUploadArea && <UploadDropArea gaCategory={'mobile'} />}
        </>
    );
});
